import { useState } from 'react'
import { Dialog, Button, Box } from '@mui/material'
import { ColStack } from 'components/atoms/container/InCardStack'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import noteTakerConsent1Img from 'assets/images/noteTakerConsent1.png'
import noteTakerConsent2Img from 'assets/images/noteTakerConsent2.png'
import { ReactComponent as UpgradeIconIcon } from 'assets/images/upgradeIcon.svg'
import { ReactComponent as LockOpenRightIcon } from 'assets/images/lockOpenRight.svg'
import { ReactComponent as HighlightMouseCursorIcon } from 'assets/images/highlightMouseCursor.svg'
import { ReactComponent as AdminPanelSettingsIcon } from 'assets/images/adminPanelSettings.svg'
import { ReactComponent as NoteTakerIcon } from 'assets/images/noteTaker.svg'
import { IconContainer } from 'components/atoms/Icons'
import { isAllowedFeature } from 'utils/permissionCheck'
import { useNavigate } from 'react-router-dom'
import { MEETINGS } from 'routes/constants'
import NavStepper from 'components/atoms/NavStepper'
import {
  AnnouncementHeader,
  Slide1,
  Slide2,
  Slide3,
  Slide4,
  Slide5
} from './AnnouncementPopupSlides'
import { useTranslation } from 'react-i18next'
import colors from 'styles/colors'
import { FEATURE_KEYS } from 'services/constants'

const OneOnOneAnnouncementDialog = ({ open, closePopup, isCalendarLinked }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const hasLicense = isAllowedFeature(FEATURE_KEYS.ONE_ON_ONE_PAID)
  const [activeSlide, setActiveSlide] = useState(0)

  const slides = [
    {
      img: noteTakerConsent1Img,
      icon: <UpgradeIconIcon />,
      heading: 'announcement.oneOnOneSlides.slide1.heading',
      body: <Slide1 />
    },
    {
      img: noteTakerConsent1Img,
      icon: <LockOpenRightIcon />,
      heading: 'announcement.oneOnOneSlides.slide2.heading',
      body: <Slide2 />
    },
    hasLicense && {
      img: noteTakerConsent1Img,
      icon: <HighlightMouseCursorIcon />,
      heading: 'announcement.oneOnOneSlides.slide3.heading',
      body: <Slide3 />
    },
    {
      img: noteTakerConsent1Img,
      icon: <AdminPanelSettingsIcon />,
      heading: 'announcement.oneOnOneSlides.slide4.heading',
      subHeading: 'announcement.oneOnOneSlides.slide4.subHeading',
      body: <Slide4 />
    },
    {
      img: noteTakerConsent2Img,
      icon: (
        <IconContainer
          icon={<NoteTakerIcon />}
          size={56}
          style={{
            '--path-color': colors.White
          }}
        />
      ),
      heading: 'announcement.oneOnOneSlides.slide5.heading',
      body: <Slide5 />
    }
  ].filter(Boolean)

  const handleSlideBack = () => {
    if (activeSlide > 0) setActiveSlide(activeSlide - 1)
  }

  const handleSlideNext = (isClose = false) => {
    if (activeSlide < slides.length - 1) {
      setActiveSlide(activeSlide + 1)
      return
    } else if (!isClose) {
      navigate(MEETINGS, {
        state: {
          openConnectCalendar: !isCalendarLinked, // open connect calendar
          openAddNoteTaker: isCalendarLinked
        }
      })
    }

    closePopup()
  }

  return (
    <Dialog
      open={open}
      sx={{
        '& .MuiDialog-paper': {
          width: 568,
          borderRadius: 3
        }
      }}>
      <ColStack spacing={0}>
        <BoxWithBackground
          sx={{
            backgroundImage: `url(${slides[activeSlide].img})`,
            borderRadius: 0,
            height: 300,
            width: '100%',
            backgroundSize: 'cover',
            padding: 16
          }}>
          <AnnouncementHeader
            icon={slides[activeSlide].icon}
            heading={slides[activeSlide].heading}
            subHeading={slides[activeSlide].subHeading}
          />
        </BoxWithBackground>

        <ColStack spacing={12} padding={12} justifyContent='space-between'>
          <Box height={activeSlide === slides.length - 1 ? 287 : 320}>
            {slides[activeSlide].body}
          </Box>

          <ColStack spacing={8} alignItems='center'>
            <NavStepper
              length={slides.length}
              activeStep={activeSlide}
              backButton={
                <Button
                  color='muted'
                  disabled={activeSlide === 0}
                  onClick={handleSlideBack}>
                  {t('announcement.previous')}
                </Button>
              }
              nextButton={
                <Button onClick={() => handleSlideNext()}>
                  {activeSlide === slides.length - 1 && hasLicense
                    ? t('announcement.startNow')
                    : t('common.continue')}
                </Button>
              }
              sx={{
                gap: 8
              }}
            />
            {activeSlide === slides.length - 1 && hasLicense && (
              <Button
                variant='text'
                size='small'
                style={{
                  padding: 0,
                  color: colors.MidnightTeal300
                }}
                onClick={() => handleSlideNext(true)}>
                {t('announcement.close')}
              </Button>
            )}
          </ColStack>
        </ColStack>
      </ColStack>
    </Dialog>
  )
}

export default OneOnOneAnnouncementDialog
